import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import TitleComponent from '@piwikpro/title-crate/Title';
import {
  withAccess,
  Pagination,
  errorBoundary,
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { ListSidebarPreference } from '@piwikpro/user-preferences';
import {
  Sidebar,
  View,
  SplitView,
  withTranslation,
} from '@piwikpro/ui-components';
import { Outlet, useLocation } from '@tanstack/react-router';
import { MetaSites as MetaSitesService } from '@piwikpro/meta-sites-crate';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import { OrganizationState } from '@piwikpro/organization-crate';
import { SidebarLimitBanner } from '../SidebarLimitBanner/SidebarLimitBanner';
import MetaSitesListOverlay from './MetaSitesListOverlay';

const ASCENDING = '';
const DESCENDING = '-';

const mapStateToProps = (state: MetaSitesState & { organization: OrganizationState }) => ({
  metaSites: state.metaSites.list.metaSites,
  metaSite: state.metaSites.details.settings.settings.attributes,
  isLoading: state.metaSites.list.isLoading || state.organization.details.isLoading,
  filters: state.metaSites.list.filters,
  failed: state.metaSites.list.failed,
  total: state.metaSites.list.total,
  metaSitesLimit: state.organization?.details?.license?.current?.plan?.administration?.meta_sites_limit,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  metaSitesApi: MetaSitesService
  accessManager: any
  config: ConfigService
  userPreferences: any
  Title: typeof TitleComponent
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

interface State {
  page: number
  search: string
}

function MetaSitesListElement({
  id, name, caption, onClick,
}) {
  const location = useLocation();
  const isActive = location.pathname.includes(id);
  return (
    <Sidebar.Element
      id={id}
      name={name}
      caption={caption}
      isActive={isActive}
      onClick={onClick}
    />
  );
}


class MetaSites extends React.Component<Props, State> {
  private paginationLimit: number;

  private listSidebarPreference: any;

  constructor(props: Props) {
    super(props);

    const { config, userPreferences } = this.props;

    this.state = {
      page: 2, // first page already loads on componentDidMount
      search: '',
    };

    this.paginationLimit = config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE');
    this.listSidebarPreference = new ListSidebarPreference(
      userPreferences.load(),
      '/administration/meta-sites', {
        sortBy: config.get('META_SITES_DEFAULT_SORT'),
      },
    );

    this.moveToDetailsView = this.moveToDetailsView.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.loadMoreListItems = this.loadMoreListItems.bind(this);
    this.moveToCreateView = this.moveToCreateView.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
  }

  async componentDidMount() {
    const { metaSitesApi, config } = this.props;

    try {
      await metaSitesApi.fetchMetaSites({
        sort: {
          by: this.listSidebarPreference.sortBy,
          direction: this.listSidebarPreference.sortBy === config.get('USER_GROUP_DEFAULT_SORT')
            ? ASCENDING
            : DESCENDING,
        },
        search: '',
      });
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  handleSearchChange(searchValue) {
    this.setState({
      search: searchValue.target.value,
    });
  }

  async handleSearch() {
    const { metaSitesApi, filters } = this.props;
    const { search } = this.state;

    if (!/^\s+$/i.test(search)) { // avoid API requests for white-characters only
      const trimmedSearch = search.trim();

      this.setState({
        page: 2,
      });

      try {
        await metaSitesApi.fetchMetaSites({
          sort: {
            by: filters.sort.by,
            direction: filters.sort.direction,
          },
          search: trimmedSearch,
        });
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }
  }

  moveToDetailsView(metaSite) {
    const { routerService } = this.props;

    routerService.push({ to: '/administration/meta-sites/$metaSiteId', params: { metaSiteId: String(metaSite) } });
  }

  async loadMoreListItems(offset, limit) {
    const { page } = this.state;
    const { metaSitesApi, filters } = this.props;

    this.setState({
      page: page + 1,
    });
    try {
      await metaSitesApi.fetchMetaSites(filters, offset, limit);
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  moveToCreateView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/m/create');
  }

  render() {
    const {
      t,
      metaSites,
      filters,
      total,
      metaSitesLimit,
      metaSitesApi,
      accessManager,
      isLoading,
      failed,
      config,
      Title,
      userPreferences,
    } = this.props;
    const { page, search } = this.state;
    let pagination;

    if (metaSites.length !== 0) {
      pagination = Pagination.fromQueryParams(
        metaSites,
        metaSites.length,
        {
          limit: this.paginationLimit,
          page,
        },
      );
    }
    const isMetaSitesLimitReached = typeof metaSitesLimit === 'number'
      && metaSitesLimit >= 0
      && total >= metaSitesLimit;
    const isOwner = accessManager.isGranted({ role: 'OWNER' });
    const isAddMetaSiteButtonDisabled = isLoading || !isOwner || isMetaSitesLimitReached;
    let addMetaSiteButtonTooltip: string | undefined;
    if (!isOwner) {
      addMetaSiteButtonTooltip = t('accessControl.permissions.role.owner.required.info');
    } else if (isMetaSitesLimitReached) {
      addMetaSiteButtonTooltip = t('meta-sites.list.createButtonTooltip.limitReached');
    }
    const listSidebarOptions = {
      sortItems: [{
        id: 'meta-sites-sidebar-sort-options',
        items: [{
          name: t('meta-sites.list.sortOptions.name'),
          value: 'name',
          id: 'name',
        },
        {
          name: t('meta-sites.list.sortOptions.addedAt'),
          value: 'created_at',
          id: 'created_at',
        },
        {
          name: t('meta-sites.list.sortOptions.updatedAt'),
          value: 'updated_at',
          id: 'updated_at',
        }],
      }],
      onSortByItemSelected: async (sortByValue) => {
        this.listSidebarPreference.sortBy = sortByValue;

        userPreferences.persist(this.listSidebarPreference);

        this.setState({
          page: 2,
        });

        try {
          await metaSitesApi.fetchMetaSites({
            sort: {
              by: this.listSidebarPreference.sortBy,
              direction: this.listSidebarPreference.sortBy === 'name' ? ASCENDING : DESCENDING,
            },
            search,
          });
          // eslint-disable-next-line no-empty
        } catch (err) {}
      },
      sortDefault: filters.sort.by,
    };

    const getLabelForMetaSite = (metaSite) => {
      let result;

      switch (filters.sort.by) {
        case 'created_at':
          result = t('components:date.short', { date: new Date(metaSite.attributes.created_at) });
          break;
        case 'updated_at':
          result = t('components:date.short', { date: new Date(metaSite.attributes.updated_at) });
          break;
        default:
          result = null;
          break;
      }

      return result;
    };

    return (
      <Title part={t('meta-sites.title')}>
        <SplitView
          leftSidebar={{
            canBeHideManually: true,
            sidebar: (
              <div style={{ width: '400px', height: '100%' }}>
                <Sidebar>
                  <Sidebar.Search
                    value={search}
                    placeholder={t('meta-sites.list.searchPlaceholder')}
                    onSearchChange={this.handleSearchChange}
                    onSearch={this.handleSearch}
                    debounceTime={config.get('SIDEBAR_LISTS_SEARCH_DELAY')}
                  />
                  <Sidebar.ListOptions
                    sort={{
                      list: listSidebarOptions.sortItems,
                      onChange: listSidebarOptions.onSortByItemSelected,
                      value: this.listSidebarPreference.sortBy,
                    }}
                  />
                  {isOwner && typeof metaSitesLimit === 'number' && metaSitesLimit >= 0 && (
                    <SidebarLimitBanner limit={metaSitesLimit} current={total} />
                  )}
                  {!isLoading && metaSites.length === 0 && (
                    <Sidebar.Empty type="subdued">
                      {t('meta-sites.list.no-items')}
                    </Sidebar.Empty>
                  )}
                  {failed && (
                    <Sidebar.Empty type="error">
                      {t('ListSidebar.error')}
                    </Sidebar.Empty>
                  )}
                  {isLoading ? (
                    <Sidebar.ElementsLoading />
                  ) : (
                    <Sidebar.List>
                      {metaSites.map(metaSite => (
                        <MetaSitesListElement
                          id={metaSite.id}
                          key={metaSite.id}
                          name={metaSite.attributes.name}
                          caption={getLabelForMetaSite(metaSite)}
                          onClick={this.moveToDetailsView}
                        />
                      ))}
                    </Sidebar.List>
                  )}
                  {metaSites.length < total && (
                    <Sidebar.LoadMore
                      onClick={() => this.loadMoreListItems(pagination.offset, this.paginationLimit)}
                    />
                  )}
                  <Sidebar.AddSection
                    mainButton={{
                      tooltip: addMetaSiteButtonTooltip ? { content: addMetaSiteButtonTooltip } : undefined,
                      onClick: this.moveToCreateView,
                      text: t('meta-sites.list.createButton'),
                      disabled: isAddMetaSiteButtonDisabled,
                    }}
                  />
                </Sidebar>
              </div>
            ),
          }}
        >
          <View fullWidth fullHeight>
            <View.Body>
              <Outlet />
              <MetaSitesListOverlay />
            </View.Body>
          </View>
        </SplitView>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  metaSitesApi: 'MetaSitesCrate.metaSites',
  userPreferences: 'userPreferences',
  config: 'config',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(withAccess(withTranslation('administration')(errorBoundary(MetaSites as any)))));
